// Record-set diff — implemented ONCE against the canonical model and reused by
// every structural format. Returns per-record detail so each front-end can
// surface the right granularity. Honours ignore-rules (ignored fields, case,
// whitespace) so toggling a chip re-runs the same logic client-side.
import { typeOf, valueEqual } from './flatten.js';
import { ROOT_KEY } from './model.js';
import { isIgnoredPath } from './path.js';
import { makeNormalizer } from '../normalize.js';

export function diffRecordSet(left, right, options = {}) {
  const ignore = new Set(options.ignoreFields || []);
  // One normalizer per comparison — the same one the line engines use, so
  // "ignore whitespace/case" means one thing in every format.
  const norm = makeNormalizer(options);
  const rightByKey = new Map(right.records.map((r) => [r.key, r]));
  const leftByKey = new Map(left.records.map((r) => [r.key, r]));

  const matched = [];
  const added = [];
  const removed = [];
  const fieldCounts = { added: 0, removed: 0, modified: 0, unchanged: 0, typeChanges: 0 };

  for (const l of left.records) {
    const r = rightByKey.get(l.key);
    if (!r) {
      removed.push(l);
      continue;
    }
    const fieldChanges = diffFields(l.fields, r.fields, fieldCounts, l.key === ROOT_KEY ? null : l.key, ignore, norm);
    matched.push({
      key: l.key,
      leftIndex: l.index,
      rightIndex: r.index,
      leftFields: l.fields,
      rightFields: r.fields,
      fieldChanges,
      changed: fieldChanges.length > 0,
    });
  }
  for (const r of right.records) {
    if (!leftByKey.has(r.key)) added.push(r);
  }
  return { matched, added, removed, fieldCounts };
}

function diffFields(lf, rf, counts, recordKey, ignore, norm) {
  const changes = [];
  for (const [path, ov] of Object.entries(lf)) {
    if (isIgnoredPath(path, ignore)) {
      counts.unchanged++;
      continue;
    }
    if (!(path in rf)) {
      changes.push({ kind: 'remove', path, recordKey, oldValue: ov });
      counts.removed++;
      continue;
    }
    const nv = rf[path];
    const ot = typeOf(ov);
    const nt = typeOf(nv);
    if (ot !== nt) {
      changes.push({ kind: 'type', path, recordKey, oldValue: ov, newValue: nv, oldType: ot, newType: nt, breaking: true });
      counts.typeChanges++;
    } else if (!valuesEqual(ov, nv, ot, norm)) {
      changes.push({ kind: 'change', path, recordKey, oldValue: ov, newValue: nv });
      counts.modified++;
    } else {
      counts.unchanged++;
    }
  }
  for (const [path, nv] of Object.entries(rf)) {
    if (isIgnoredPath(path, ignore)) continue;
    if (!(path in lf)) {
      changes.push({ kind: 'add', path, recordKey, newValue: nv });
      counts.added++;
    }
  }
  return changes;
}

function valuesEqual(a, b, t, norm) {
  if (t === 'string') return norm(a) === norm(b);
  return valueEqual(a, b, t);
}
